import {
  Flex,
  Button,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  HStack,
  DrawerHeader,
  IconButton,
  useColorModeValue,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Alert,
  AlertDescription,
  AlertIcon,
  Spinner,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { colors } from '~/packages/common-frontend/colors';

export type ReceivableError = { message: string; testReceivableId: string };

export type ReceivableUpload = {
  loanProductId: string;
  loanProductName: string;
  creditFacilityId?: string;
  key: string;
  isValid: boolean | null; // null indicates either no file has been added or that file hasn't been validated yet
  fileError: string;
  errors: ReceivableError[];
  isLoading: boolean;
  file: File | null;
};

export const initialUploadState = {
  isValid: null,
  errors: [],
  file: null,
  fileError: '',
  isLoading: false,
  creditFacilityId: undefined,
};

export type LoanProductCreditFacilityReceivableUploadMap = {
  [key: string]: ReceivableUpload;
};

export const ActionsDrawerShell = ({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) => {
  return (
    <Drawer onClose={onClose} isOpen size="xl" closeOnOverlayClick>
      <DrawerOverlay />
      <DrawerContent p={0}>{children}</DrawerContent>
    </Drawer>
  );
};

export const ActionsDrawerHeader = ({
  onClose,
  actionsDrawerState,
}: {
  onClose: () => void;
  actionsDrawerState: ActionsDrawerState;
}) => {
  return (
    <HStack
      justifyContent="space-between"
      alignItems="center"
      w="100%"
      p="40px 40px 24px"
      borderBottom="1px solid"
      borderBottomColor="gray.100"
    >
      <DrawerHeader flexGrow={1} maxWidth="90%" p="0px">
        {_.capitalize(String(actionsDrawerState))} by CSV
      </DrawerHeader>
      <IconButton
        aria-label="Close"
        icon={<FaIcon icon="xmark-large" />}
        onClick={onClose}
        size="sm"
        variant="v2-ghost"
      />
    </HStack>
  );
};

export const ActionsDrawerFooter = ({
  onSubmit,
  kickingOffJob,
  submitDisabled,
}: {
  onSubmit: () => void;
  kickingOffJob: boolean;
  submitDisabled: boolean;
}) => {
  return (
    <Flex justifyContent="flex-end">
      <Button
        colorScheme="v2-aurora"
        onClick={onSubmit}
        isDisabled={kickingOffJob || submitDisabled}
      >
        Submit
      </Button>
    </Flex>
  );
};

export const UploadFileModal = ({
  isOpen,
  onClose,
  children,
}: {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent padding="20px" width="auto" minW="550px">
        <ModalHeader>Upload File</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{children}</ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const UploadFileErrorAlert = () => {
  return (
    <Alert status="error" m="24px 0px">
      <AlertIcon />
      <AlertDescription>
        <Typography TextProps={{ fontWeight: 500 }}>
          Upload a valid CSV file with no header and a single column containing
          only the Receivable IDs to proceed.
        </Typography>
      </AlertDescription>
    </Alert>
  );
};

export const UploadState = ({
  receivableUpload,
  openUploadModal,
  kickingOffJob,
}: {
  receivableUpload: ReceivableUpload;
  openUploadModal: () => void;
  kickingOffJob: boolean;
}) => {
  const uploadColor = useColorModeValue(colors.blue[400], colors.white[700]);
  const successColor = useColorModeValue(colors.green[400], colors.green[300]);
  const errorColor = useColorModeValue(colors.red[400], colors.red[300]);

  if (!receivableUpload.file) {
    return (
      <Button
        size="md"
        colorScheme="v2-aurora"
        variant="secondary"
        onClick={openUploadModal}
        background="white"
        ml="12px"
        isDisabled={kickingOffJob}
      >
        Upload
      </Button>
    );
  }
  if (receivableUpload.isLoading) {
    return (
      <Flex alignItems="center">
        <Spinner color={uploadColor} />
        <Button
          size="md"
          colorScheme="v2-aurora"
          variant="secondary"
          background="white"
          ml="12px"
          isDisabled
        >
          Upload
        </Button>
      </Flex>
    );
  }
  if (receivableUpload.isValid) {
    return (
      <Flex alignItems="center">
        <FaIcon color={successColor} icon="check-circle" size="16px" />
        <Typography
          variant="base-muted"
          TextProps={{ fontSize: '14px', ml: '12px' }}
        >
          Uploaded
        </Typography>
      </Flex>
    );
  }
  return (
    <Flex alignItems="center">
      <FaIcon color={errorColor} icon="circle-xmark" size="16px" />
      <Button
        size="md"
        colorScheme="v2-aurora"
        variant="secondary"
        onClick={openUploadModal}
        background="white"
        ml="12px"
        isDisabled={kickingOffJob}
      >
        Re-upload
      </Button>
    </Flex>
  );
};

export type ActionsDrawerState = 'repurchase' | 'transfer';
